import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import { Button } from 'antd'
import {
  useAllThemes,
  useI18n,
  useRehabSelectedThemes,
  useUhOhStatus,
  useMCAInfo,
} from 'contexts/AuthContext'
import ContentHeader from 'views/Care/ContentHeader'
import _ from 'lodash'
import { getSkuThemesPromise } from 'apis/FitOnCareAPI'
import noPainIcon from './img/ic-no-pain.svg'
import classes from './index.module.scss'
import { ScreenKey } from 'views/Care/util'
import { useDeeplink } from 'hooks/useDeeplink'
import { logAmplitudeEvent } from 'utils/amplitudeInit'
import { setBrazeUserAttribute } from 'utils/brazeInit'
import useUpdateQuickNotes from 'hooks/useUpdateQuickNotes'
import { getLocalLanguage } from 'utils/authLocalStorage'
import { UhOhStatus } from 'views/Care/UhOh'
import { useMCA } from '../../MCA/useMCA'
import { useMetadataOptions } from 'hooks/careHooks'
import { LoadingContent } from 'components/LoadingDialog/index'
const { styles } = classes

const NoPain = {
  coverUrl: noPainIcon,
  name: 'No Pain',
  id: -1,
}
export const usePainLocated = ({ sku }) => {
  const { allThemes, setAllThemes } = useAllThemes()
  const { rehabSelectedThemes, setRehabSelectedThemes } =
    useRehabSelectedThemes()
  const [selectedThemes, setSelectedThemes] = useState(
    rehabSelectedThemes || []
  )
  const { gender } = useDeeplink()
  const { mcaMskBodyAreas } = useMetadataOptions()

  useEffect(() => {
    const isMCA = sku === 'mca'
    if (isMCA) {
      setAllThemes([...(mcaMskBodyAreas || [])])
    } else {
      getSkuThemesPromise({ gender, sku }).then((resp) => {
        setAllThemes([...(resp?.themes || []), NoPain])
      })
    }
  }, [gender, sku, mcaMskBodyAreas])

  useEffect(() => {
    setRehabSelectedThemes(selectedThemes)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedThemes])

  return { themes: allThemes, selectedThemes, setSelectedThemes }
}

const PainLocated = (props) => {
  const { sku } = props
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { i18n } = useI18n()
  const { themes, selectedThemes, setSelectedThemes } = usePainLocated({ sku })
  const { updateQuickNotes } = useUpdateQuickNotes()
  const { setUhOhStatus } = useUhOhStatus()
  const { getNextScreenKey: getNextMCAScreenKey, showCheckingSpinner } = useMCA(
    { sku }
  )
  const { mcaInfo, setMCAInfo } = useMCAInfo()
  const isMCA = sku === 'mca'

  useEffect(() => {
    switch (sku) {
      case 'mca':
        logAmplitudeEvent('Screen View: Onboarding - MSK Body Area', {
          sku,
          language: getLocalLanguage(),
        })
        break
      default:
        logAmplitudeEvent('Screen View: Onboarding - Recent Pain', {
          sku,
          language: getLocalLanguage(),
        })
        break
    }
  }, [sku])

  const onSelectTheme = (item) => {
    if (item.id === -1) {
      if (selectedThemes.some((theme) => theme.id === item.id)) {
        setSelectedThemes([])
      } else {
        setSelectedThemes([item])
      }
    } else {
      if (selectedThemes.some((theme) => theme.id === item.id)) {
        setSelectedThemes(
          selectedThemes.filter(
            (theme) => theme.id !== item.id && theme.id !== -1
          )
        )
      } else {
        const filteredThemes = selectedThemes.filter((theme) => theme.id !== -1)
        setSelectedThemes([...filteredThemes, item])
      }
    }
  }

  const onSubmit = async (items) => {
    setIsSubmitting(true)
    const theme = items.map((item) => item.name).join(', ')
    const themeArray = items.map((item) => item.name)
    switch (sku) {
      case 'pt':
        setBrazeUserAttribute('pt_condition', themeArray)
        updateQuickNotes(`pt:[${themeArray.join(', ')}]`, 'pt')
        if (items.length === 1 && items[0].id === -1) {
          setUhOhStatus(UhOhStatus.PAIN_LOCATED)
          props.setCurrentKey(ScreenKey.UH_OH)
        } else {
          props.setCurrentKey(ScreenKey.REHAB_QUESTION_2)
        }
        logAmplitudeEvent('Click: Onboarding - Recent Pain', {
          sku,
          theme,
          language: getLocalLanguage(),
        })
        break
      case 'mca':
        logAmplitudeEvent('Click: Onboarding - MSK Body Area - Submit', {
          sku,
          areas: theme,
          language: getLocalLanguage(),
        })
        setMCAInfo({ ...mcaInfo, mskBodyAreas: items })
        const nextScreenKey = await getNextMCAScreenKey(
          ScreenKey.PAIN_LOCATED,
          {
            mskBodyAreas: items,
          }
        )
        props.setCurrentKey(nextScreenKey)
        break
      default:
        if (items.length === 1 && items[0].id === -1) {
          setUhOhStatus(UhOhStatus.PAIN_LOCATED)
          props.setCurrentKey(ScreenKey.UH_OH)
        } else {
          props.setCurrentKey(ScreenKey.REHAB_QUESTION_2)
        }
        logAmplitudeEvent('Click: Onboarding - Recent Pain', {
          sku,
          theme,
          language: getLocalLanguage(),
        })
        break
    }
    setIsSubmitting(false)
  }

  return (
    <div className={classNames(styles, 'info-sec')}>
      <ContentHeader {...props} />
      <div className='info-content'>
        {showCheckingSpinner ? (
          <div className='loading-content'>
            <LoadingContent text={i18n('Checking your eligibility…')} />
          </div>
        ) : (
          <>
            <div className='title'>
              {i18n(
                `${isMCA ? 'Where is your pain?' : 'Where is your pain located?'}`
              )}
            </div>
            <div className='subtitle' hidden={isMCA}>
              {i18n(
                'To be eligible, you must have experienced persistent pain, stiffness, or discomfort in the last two weeks.'
              )}
            </div>
            <div className='pain-items'>
              {themes.map((item) => (
                <div
                  key={item.id}
                  className={classNames('pain-item', {
                    selected: selectedThemes?.some(
                      (theme) => theme.id === item.id
                    ),
                  })}
                  onClick={() => onSelectTheme(item)}
                >
                  <img
                    src={item.coverUrl}
                    alt={item.name}
                    className='pain-item-image'
                  />
                  <div
                    className={classNames('pain-name', {
                      'gradient-font-1': selectedThemes?.some(
                        (theme) => theme.id === item.id
                      ),
                    })}
                  >
                    {i18n(item.name)}
                  </div>
                </div>
              ))}
            </div>

            <div className='bottom-btn'>
              <Button
                className='btn-primary full-width-btn next-btn'
                disabled={_.isEmpty(selectedThemes) || isSubmitting}
                onClick={() => onSubmit(selectedThemes)}
              >
                {i18n('Next')}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default PainLocated
